import {
  CheckpointTokenError,
  normalizeCheckpointToken,
} from "../../../../packages/shared/src/checkpoint-token.ts";

// Web NFC (NDEFReader) support + scanned-tag handling. Progressive enhancement ONLY: Web NFC
// exists solely on Android Chrome, so the affordance is gated on device.hasWebNfc and never shown
// on iOS/desktop (QR stays the web primary per the pilot decision). Checkpoint tags carry an NDEF
// TEXT record with the opaque chk_ token — same artifact contract as native (ADR-005), validated
// through the same shared normalizeCheckpointToken so web and native accept/reject identically.
// Invalid tags NEVER echo their payload (mirrors the mobile invalid-tag copy rule).

/** The slice of a Web NFC NDEFRecord we read — structural, so tests need no real NFC stack. */
export interface WebNdefRecordLike {
  recordType?: string;
  /** BufferSource payload (DataView in real Web NFC). */
  data?: ArrayBufferView | null;
  /** Text-record encoding ("utf-8" | "utf-16"); absent means utf-8. */
  encoding?: string | null;
}

export interface WebNdefMessageLike {
  records?: readonly WebNdefRecordLike[];
}

/** Decode the first non-empty NDEF TEXT record from a Web NFC reading. null when none decodes. */
export function extractTextFromNdefMessage(
  message: WebNdefMessageLike | null | undefined,
): string | null {
  for (const record of message?.records ?? []) {
    if (record.recordType !== "text" || !record.data) {
      continue;
    }
    try {
      const text = new TextDecoder(record.encoding ?? "utf-8").decode(record.data).trim();
      if (text.length > 0) {
        return text;
      }
    } catch {
      // Unknown encoding / undecodable payload — try the next record.
    }
  }
  return null;
}

export type ScannedTagResult = { ok: true; token: string } | { ok: false; reason: string };

/**
 * Validate a decoded tag text against the shared opaque-token contract. A non-checkpoint tag
 * (URL, random NDEF text, garbage) is rejected with a SAFE guard-facing reason that steers back
 * to QR — the payload is never echoed.
 */
export function resolveScannedTag(raw: string | null): ScannedTagResult {
  if (raw === null) {
    return { ok: false, reason: "That tag isn't a checkpoint tag — try the QR code instead." };
  }
  try {
    return { ok: true, token: normalizeCheckpointToken(raw) };
  } catch (error) {
    if (error instanceof CheckpointTokenError) {
      return { ok: false, reason: "That tag isn't a checkpoint tag — try the QR code instead." };
    }
    return { ok: false, reason: "Couldn't read that tag." };
  }
}
